import { getAiTeammateWidgetService, type AiTeammateWidgetData } from '@playbooks/action-cards-utils/src'
import type { FunctionComponent, ReactNode } from 'react'
import { useState, useRef, useEffect } from 'react'

import { type WidgetProps } from './widgetTypes'
import { CustomButton } from '../../components/ui/CustomButton'
import { traverseNodes } from '../../utils/actionCardOperations'
import { Mic, MicOff, X, CheckCircle2, ChevronDown, Clipboard, ClipboardCheck } from 'lucide-react'
import { useTranslation } from '../../contexts/localization/Localization'
import { applyAiResponse } from '../../services/ai/applyAiResponse'
import type { GeneratedPlayResponse } from '../../services/playbooksBackendClient'
import { usePlaybooksBackendClient } from '../../hooks/usePlaybooksBackendClient'
import { useAppContext } from '../../contexts/AppContext'
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard'
import useMountEffect from '../../hooks/useMountEffect'

// Global Window Types for Developer Debugging
declare global {
  interface Window {
    constants?: {
      aiTeammateWidget?: {
        showLog?: boolean
      }
    }
  }
}

const CAPTURE_APP_WS_URL = 'ws://localhost:49521'

interface AiTeammateWidgetProps extends WidgetProps {
  widget: AiTeammateWidgetData
}

interface CollapsibleHeaderProps {
  title: ReactNode
  isOpen: boolean
  onToggle: () => void
}

interface RealtimeEvent {
  type: string
  response?: {
    output?: Array<{
      type?: string
      name?: string
      arguments?: string
    }>
  }
  transcript?: string
  error?: {
    type?: string
    message?: string
  }
}

/** Minimum confidence required to apply the answer to the Action Card */
const minAnswerConfidence = 0.2

/** Safety timeout duration in milliseconds (2 hours) */
const SAFETY_TIMEOUT_MS = 2 * 60 * 60 * 1000

/** Debounce duration for persisting transcript changes */
const TRANSCRIPT_SAVE_DEBOUNCE_MS = 10000

/**
 * AI Teammate Widget - Real-time speech-to-action-card processing
 * Listens to user's microphone and fills out the Action Card based on speech using OpenAI Realtime API.
 * Emphasizes low latency and high responsiveness for real-time call assistance.
 * @story User Story 13796: AI Teammate - reduce latency
 */
const AiTeammateWidget: FunctionComponent<AiTeammateWidgetProps> = ({ widget, context }) => {
  const { data: state, update: updateState, getActualState } = useAppContext()

  const playbooksBackendClient = usePlaybooksBackendClient()

  // State
  const [isListening, setIsListening] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [captureAppConnected, setCaptureAppConnected] = useState(false)
  const [generatedResponses, setGeneratedResponses] = useState<GeneratedPlayResponse[]>([])
  const [questionLabels, setQuestionLabels] = useState<Record<number, string>>({})
  const [answerLabels, setAnswerLabels] = useState<Record<number, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [showLog, setShowLog] = useState(false)
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false)
  const [transcript, setTranscript] = useState(widget.value?.transcript?.trim() ?? '')
  const { copyToClipboard: copyTranscriptToClipboard, isCopied: isTranscriptCopied } = useCopyToClipboard()

  // Refs for WebRTC
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null)
  const dataChannelRef = useRef<RTCDataChannel | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const transcriptSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Keep a ref to the latest widget data for use in async callbacks without stale closures
  const widgetRef = useRef(widget)
  // Disable lint warning: we are only updating the ref, not actually using it for rendering
  // eslint-disable-next-line react-hooks/refs
  widgetRef.current = widget

  // Refs for capture app WebSocket and audio processing
  const captureWsRef = useRef<WebSocket | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null)
  const audioQueueRef = useRef<{ chunks: Float32Array[]; readPos: number }>({ chunks: [], readPos: 0 })
  const usingCaptureAppRef = useRef(false)

  // Translation
  const { text: t } = useTranslation()

  // Determines if the extraction log is visible
  const isLogVisible = widget.showLog || !!window?.constants?.aiTeammateWidget?.showLog
  const shouldRecordTranscript = widget.recordTranscript ?? false
  const shouldFillAutomatically = widget.fillAutomatically ?? true

  /**
   * Persist any pending transcript change immediately.
   */
  function flushTranscriptSave() {
    // Clear the transcript save timer
    const hasPendingSave = !!transcriptSaveTimerRef.current
    clearTranscriptSaveTimer()

    if (hasPendingSave) {
      // Persist the Action Card state
      updateState()
    }
  }

  /**
   * Clear the transcript save timer without persisting changes
   */
  function clearTranscriptSaveTimer() {
    if (transcriptSaveTimerRef.current) {
      clearTimeout(transcriptSaveTimerRef.current)
      transcriptSaveTimerRef.current = null
    }
  }

  /**
   * Cleanup WebRTC and capture app audio resources
   */
  function cleanup() {
    // Close data channel
    if (dataChannelRef.current) {
      dataChannelRef.current.close()
      dataChannelRef.current = null
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close()
      peerConnectionRef.current = null
    }

    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop())
      mediaStreamRef.current = null
    }

    // Cleanup capture app audio processing
    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect()
      scriptProcessorRef.current = null
    }
    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }
    audioQueueRef.current = { chunks: [], readPos: 0 }
    if (captureWsRef.current) {
      captureWsRef.current.onmessage = null
    }
    usingCaptureAppRef.current = false
  }

  /**
   * Start safety timer to automatically stop listening after timeout
   */
  function startSafetyTimer() {
    clearSafetyTimer()
    safetyTimerRef.current = setTimeout(() => {
      console.log('Safety timeout reached, stopping listening')
      stopListening()
      setError(t('aiTeammateWidget_timeoutReached'))
    }, SAFETY_TIMEOUT_MS)
  }

  /**
   * Clear the safety timer
   */
  function clearSafetyTimer() {
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current)
      safetyTimerRef.current = null
    }
  }

  // Initialize browser environment tracking flags for developer console overrides
  useMountEffect(() => {
    if (!window) return

    window.constants = window.constants || {}
    window.constants.aiTeammateWidget = window.constants.aiTeammateWidget || {}

    console.log(
      "💡 [AI Teammate] Run 'window.constants.aiTeammateWidget.showLog = true' in your terminal to reveal extracted log.",
    )
  })

  // Probe capture app WebSocket on mount
  // (check if audio capture app is running and available for system audio streaming)
  useEffect(() => {
    try {
      const ws = new WebSocket(CAPTURE_APP_WS_URL)
      ws.binaryType = 'arraybuffer'

      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) ws.close()
      }, 3000)

      ws.onopen = () => {
        clearTimeout(connectionTimeout)
        captureWsRef.current = ws
        setCaptureAppConnected(true)
      }

      ws.onclose = () => {
        clearTimeout(connectionTimeout)
        if (captureWsRef.current === ws) {
          captureWsRef.current = null
          setCaptureAppConnected(false)
        }
      }

      return () => {
        clearTimeout(connectionTimeout)
        ws.onclose = null
        ws.close()
        captureWsRef.current = null
      }
    } catch (err) {
      console.warn('Could not connect to capture app WebSocket:', err)
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      flushTranscriptSave()
      cleanup()
      clearSafetyTimer()
    }
  }, [])

  /**
   * Start listening — uses capture app WebSocket if available, otherwise falls back to mic
   */
  async function startListening() {
    if (captureAppConnected && captureWsRef.current?.readyState === WebSocket.OPEN) {
      // Listen to the mixed system audio + mic stream from the capture app
      await startListeningWithCaptureApp()
    } else {
      // Listen directly to the microphone (will not include system audio)
      await startListeningWithMic()
    }
  }

  /**
   * Start listening to the microphone
   * and processing live audio using OpenAI Realtime API
   */
  async function startListeningWithMic() {
    try {
      setIsConnecting(true)
      setError(null)
      usingCaptureAppRef.current = false

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
      mediaStreamRef.current = stream

      await connectWebRTC(stream)
    } catch (err) {
      console.error('Error starting listening:', err)
      setError(err instanceof Error ? err.message : 'Failed to start listening')
      setIsConnecting(false)
      cleanup()
    }
  }

  /**
   * Start listening using the native capture app audio stream via WebSocket.
   * Receives stereo Float32Array audio at 48 kHz, mixes to mono, and feeds it
   * into a MediaStream used for the WebRTC connection to OpenAI.
   */
  async function startListeningWithCaptureApp() {
    try {
      setIsConnecting(true)
      setError(null)
      usingCaptureAppRef.current = true

      const ws = captureWsRef.current!

      // Create AudioContext to convert WebSocket audio into a MediaStream
      const audioContext = new AudioContext({ sampleRate: 48000 })
      audioContextRef.current = audioContext

      // Reset audio queue
      audioQueueRef.current = { chunks: [], readPos: 0 }

      // Buffer incoming binary audio frames into the queue
      ws.onmessage = (event: MessageEvent) => {
        if (event.data instanceof ArrayBuffer) {
          audioQueueRef.current.chunks.push(new Float32Array(event.data))
        }
      }

      // ScriptProcessorNode drains the queue and outputs mono audio
      const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1)
      scriptProcessorRef.current = scriptProcessor

      scriptProcessor.onaudioprocess = (e: AudioProcessingEvent) => {
        const output = e.outputBuffer.getChannelData(0)
        const queue = audioQueueRef.current
        let outIdx = 0

        while (outIdx < output.length) {
          if (queue.chunks.length === 0) {
            output[outIdx++] = 0
            continue
          }

          const chunk = queue.chunks[0]
          const chunkFrames = chunk.length / 2 // stereo interleaved

          if (queue.readPos >= chunkFrames) {
            queue.chunks.shift()
            queue.readPos = 0
            continue
          }

          // Mix stereo L+R to mono
          const i = queue.readPos * 2
          output[outIdx++] = (chunk[i] + chunk[i + 1]) * 0.5
          queue.readPos++
        }
      }

      const destination = audioContext.createMediaStreamDestination()
      scriptProcessor.connect(destination)
      const stream = destination.stream
      mediaStreamRef.current = stream

      // Tell the capture app to start streaming
      ws.send(JSON.stringify({ type: 'start-capture' }))

      await connectWebRTC(stream)
    } catch (err) {
      console.error('Error starting with capture app:', err)
      setError(err instanceof Error ? err.message : 'Failed to start listening')
      setIsConnecting(false)
      cleanup()
    }
  }

  /**
   * Shared OpenAI WebRTC streaming setup: adds the given stream's tracks, creates a data channel,
   * exchanges SDP with the backend, and sets the listening state.
   */
  async function connectWebRTC(stream: MediaStream) {
    const savedTranscript = shouldRecordTranscript ? (widget.value?.transcript?.trim() ?? '') : ''
    setTranscript(savedTranscript)

    const pc = new RTCPeerConnection()
    peerConnectionRef.current = pc

    stream.getTracks().forEach(track => pc.addTrack(track, stream))

    const dataChannel = pc.createDataChannel('oai-events')
    dataChannelRef.current = dataChannel
    setupDataChannelListeners(dataChannel)

    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)

    const { sdpAnswer } = await playbooksBackendClient.createTeammateSession({
      sdp: offer.sdp!,
      actionCardSections: context?.headerScopeSections.map(s => JSON.stringify(s)) ?? [],
      currentDate: getClientLocalDate(),
      recordTranscript: shouldRecordTranscript,
      fillAutomatically: shouldFillAutomatically,
    })

    await pc.setRemoteDescription({ type: 'answer', sdp: sdpAnswer })

    setIsListening(true)
    setIsConnecting(false)
    startSafetyTimer()
  }

  /**
   * Get the client's local date in YYYY-MM-DD format for resolving relative dates in the speech
   */
  function getClientLocalDate() {
    const now = new Date()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${now.getFullYear()}-${month}-${day}`
  }

  /**
   * Stop listening and cleanup resources
   */
  function stopListening() {
    if (usingCaptureAppRef.current && captureWsRef.current?.readyState === WebSocket.OPEN) {
      captureWsRef.current.send(JSON.stringify({ type: 'stop-capture' }))
    }
    flushTranscriptSave()
    cleanup()
    clearSafetyTimer()
    setIsListening(false)
  }

  /**
   * Clear the responses log
   */
  function clearLog() {
    setGeneratedResponses([])
  }

  /**
   * Schedule a delayed Action Card update in order to persist the actual transcript
   */
  function scheduleTranscriptSave() {
    if (!shouldRecordTranscript) return

    // Do not schedule if already scheduled
    if (transcriptSaveTimerRef.current) return

    // Schedule Action Card update
    transcriptSaveTimerRef.current = setTimeout(() => {
      transcriptSaveTimerRef.current = null
      // Persist the Action Card state
      updateState()
    }, TRANSCRIPT_SAVE_DEBOUNCE_MS)
  }

  /**
   * Set up event listeners for the WebRTC data channel
   */
  function setupDataChannelListeners(dataChannel: RTCDataChannel) {
    dataChannel.addEventListener('open', () => {
      console.log('Data channel opened')
    })

    dataChannel.addEventListener('close', () => {
      console.log('Data channel closed')
    })

    dataChannel.addEventListener('error', err => {
      console.error('Data channel error:', err)
      setError('Connection error occurred')
    })

    dataChannel.addEventListener('message', event => {
      try {
        const message = JSON.parse(event.data) as RealtimeEvent
        handleRealtimeEvent(message)
      } catch (err) {
        console.error('Error parsing message:', err)
      }
    })
  }

  /**
   * Handle events from OpenAI Realtime API
   */
  function handleRealtimeEvent(event: RealtimeEvent) {
    console.log('Realtime event:', event.type, event)

    // Handle function call completion
    if (shouldFillAutomatically && event.type === 'response.done') {
      const outputs = event.response?.output || []

      for (const output of outputs) {
        if (
          output.type === 'function_call' &&
          output.name === 'update_action_card' &&
          typeof output.arguments === 'string'
        ) {
          try {
            const args = JSON.parse(output.arguments)
            console.log('Function call arguments:', args)
            processActionCardUpdates(args.upd.map(convertFromCompactResponse))
          } catch (err) {
            console.error('Error parsing function call arguments:', err)
          }
        }
      }
    }

    if (
      shouldRecordTranscript &&
      event.type === 'conversation.item.input_audio_transcription.completed' &&
      event.transcript
    ) {
      // Store transcript in state and schedule an Action Card update
      setTranscript(currentTranscript => {
        // Append new transcript to existing transcript with a separator
        const nextTranscript = appendTranscript(currentTranscript, event.transcript, '\n')
        // Put the transcript into the widget value without triggering a state update yet
        // (to avoid excessive updates while the user is speaking)
        getAiTeammateWidgetService(widgetRef.current).setValue({ transcript: nextTranscript })
        // Schedule an Action Card update to persist the new transcript after a debounce period
        scheduleTranscriptSave()
        return nextTranscript
      })
    }

    // Handle errors
    if (event.type === 'error') {
      if (canErrorBeIgnored(event?.error)) {
        console.error('Ignoring benign Realtime API error', { event })
        return
      }
      console.error('Realtime API error:', event.error)
      setError(event.error?.message || 'An error occurred')
    }
  }

  /**
   * Convert from compact response schema ({qId, ans, ansIds, conf}) to GeneratedPlayResponse
   */
  function convertFromCompactResponse(compact: {
    qId: number
    ans: string
    ansIds: number[]
    conf: number
  }): GeneratedPlayResponse {
    return {
      questionId: compact.qId,
      answer: compact.ans,
      answerIds: compact.ansIds,
      confidence: compact.conf,
    }
  }

  /**
   * Process updates from the AI and apply them to the Action Card
   */
  function processActionCardUpdates(updates: GeneratedPlayResponse[]) {
    if (!updates || !Array.isArray(updates)) return

    const newGeneratedResponses: GeneratedPlayResponse[] = []

    // Update question labels for display in the log
    setQuestionLabels(prev => ({ ...prev, ...getQuestionLabels(updates) }))

    for (const update of updates) {
      // Add to generated responses log
      newGeneratedResponses.push({
        ...update,
        // These log records are mostly for debugging, so purity doesn't matter
        // eslint-disable-next-line react-hooks/purity
        timestamp: Date.now(),
      })

      // Update answer labels for display
      setAnswerLabels(prev => ({ ...prev, ...getAnswerLabels(update) }))

      if (update.confidence >= minAnswerConfidence) {
        // Apply to the action card
        applyUpdateToActionCard(update)
      }
    }

    // Update the log display
    if (newGeneratedResponses.length > 0) {
      setGeneratedResponses(prev => [...newGeneratedResponses, ...prev].slice(0, 50)) // Keep last 50 updates

      // Update the Action Card state
      updateState()
    }
  }

  /**
   * Apply a single update to the Action Card
   */
  function applyUpdateToActionCard(update: GeneratedPlayResponse) {
    const latestState = getActualState().data
    traverseNodes(latestState, 'id', (node, key) => {
      if (node[key] === update.questionId) {
        applyAiResponse(update, node)
      }
    })
  }

  /**
   * Get human-readable labels for the provided question/assessment IDs
   */
  function getQuestionLabels(updates: GeneratedPlayResponse[]): Record<number, string> {
    const questionLabels: Record<number, string> = {}
    traverseNodes(state, 'id', (node, key) => {
      if (updates.some(update => update.questionId === node[key])) {
        questionLabels[node[key]] = node.text || node.question
      }
    })
    return questionLabels
  }

  /**
   * Get human-readable labels for the provided answer IDs (for dropdown/assessment questions)
   */
  function getAnswerLabels(update: GeneratedPlayResponse): Record<number, string> {
    const answerLabels: Record<number, string> = {}
    if (!update.answerIds?.length) return answerLabels
    traverseNodes(state, 'id', (node, key) => {
      if (update.answerIds?.some(id => node[key] === id)) {
        answerLabels[node[key]] = node.name || node.answer
      }
    })
    return answerLabels
  }

  /**
   * Determine if a Realtime API error is benign and can be ignored
   */
  function canErrorBeIgnored(error?: { type?: string; message?: string }): boolean {
    const message = error?.message
    return (
      // We ignore "Conversation already has an active response" error as it doesn't seem to impact the functionality
      error?.type === 'invalid_request_error' ||
      (typeof message === 'string' && message.includes('already has an active response'))
    )
  }

  return (
    <div className="space-y-4">
      {/* Control Button */}
      <div className="flex items-center gap-3">
        <CustomButton
          size={'lg'}
          variant={isListening ? 'destructive' : 'default'}
          isLoading={isConnecting}
          onClick={() => {
            if (isListening) {
              stopListening()
            } else {
              startListening()
            }
          }}
        >
          <div className="flex gap-1 items-center">
            {isConnecting ? (
              <>{t('aiTeammateWidget_starting')}</>
            ) : isListening ? (
              <>
                <MicOff className="h-5 w-5" />
                {t('aiTeammateWidget_stopListening')}
              </>
            ) : (
              <>
                <Mic className="h-5 w-5" />
                {widget.buttonLabel || t('aiTeammateWidget_defaultButton')}
              </>
            )}
          </div>
        </CustomButton>

        {captureAppConnected && !isListening && (
          <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {t('aiTeammateWidget_appConnected')}
          </div>
        )}

        {isListening && (
          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <div className="h-2 w-2 rounded-full bg-green-600 animate-pulse" />
            {t('aiTeammateWidget_listening')}
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-md">
          {error}
        </div>
      )}

      {/* Live Log */}
      {isLogVisible && generatedResponses.length > 0 && (
        <div className="mt-4">
          <CollapsibleHeader
            title={
              <>
                {t('aiTeammateWidget_extractedInformation')} {generatedResponses.length}
              </>
            }
            isOpen={showLog}
            onToggle={() => setShowLog(prev => !prev)}
          />

          {/* Conditionally show log entries */}
          {showLog && (
            <div className="relative bg-gray-50 dark:bg-gray-900 rounded-md p-3 pr-9 max-h-50 overflow-y-auto space-y-1">
              <button
                type="button"
                onClick={clearLog}
                className="absolute right-2 top-2 rounded p-1 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700"
                title={t('aiTeammateWidget_clearLog')}
                aria-label={t('aiTeammateWidget_clearLog')}
              >
                <X className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
              </button>
              {generatedResponses.map((update, index) => (
                <div key={`${update.questionId}-${update.timestamp}-${index}`} className="text-sm font-mono">
                  <span className="text-gray-500">· </span>
                  <span className="font-medium">
                    {questionLabels[update.questionId] || `${t('aiTeammateWidget_question')} #${update.questionId}`}:
                  </span>{' '}
                  <span className="text-gray-700 dark:text-gray-300">
                    {update.answer ||
                      update.answerIds
                        ?.map(id => answerLabels[id] || `${t('aiTeammateWidget_option')} #${id}`)
                        .join(', ') || <em>{t('aiTeammateWidget_noAnswer')}</em>}
                  </span>
                  {update.confidence < 0.8 && (
                    <span className="ml-2 text-xs text-yellow-600 dark:text-yellow-400">
                      {t('aiTeammateWidget_uncertain')}
                    </span>
                  )}
                  {update.confidence < minAnswerConfidence && (
                    <span className="ml-2 text-xs text-red-600 dark:text-red-400">
                      ({(update.confidence * 100).toFixed(0)}% {t('aiTeammateWidget_uncertain_ignored')})
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Transcript */}
      {shouldRecordTranscript && (
        <div className="mt-4">
          <CollapsibleHeader
            title={t('aiTeammateWidget_transcript')}
            isOpen={isTranscriptOpen}
            onToggle={() => setIsTranscriptOpen(prev => !prev)}
          />

          {isTranscriptOpen && (
            <div className="relative bg-gray-50 dark:bg-gray-900 rounded-md p-3 pr-9 max-h-50 overflow-y-auto">
              {transcript && (
                <button
                  type="button"
                  onClick={() => copyTranscriptToClipboard(transcript)}
                  className="absolute right-2 top-2 rounded p-1 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700"
                  title={t('copyToClipboard')}
                  aria-label={t('copyToClipboard')}
                >
                  {isTranscriptCopied ? (
                    <ClipboardCheck className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                  ) : (
                    <Clipboard className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                  )}
                </button>
              )}
              <pre className="text-sm font-mono whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                {transcript || <em>{t('aiTeammateWidget_noTranscript')}</em>}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function appendTranscript(
  existingTranscript: string | undefined,
  sessionTranscript: string | undefined,
  separator = '\n\n',
) {
  return [existingTranscript, sessionTranscript]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(separator)
}

function CollapsibleHeader({ title, isOpen, onToggle }: CollapsibleHeaderProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between mb-2 text-left"
      aria-expanded={isOpen}
    >
      <h4 className="text-sm font-medium">{title}</h4>
      <ChevronDown
        className={`h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
  )
}

export default AiTeammateWidget
